import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader, MessageSquare, Activity, DollarSign, Download, RefreshCw, BarChart3, BarChart4, Calendar } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/context/auth';
import UsageReportSelector from './UsageReportSelector';
import { useSmsUsageReports, TimeframeType } from '@/hooks/sms/useSmsUsageReports';
import EnhancedDateRangePicker from '@/components/analytics/EnhancedDateRangePicker';
import { DateRange } from 'react-day-picker';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

export const RestaurantUsageStats: React.FC = React.memo(() => {
  const { user } = useAuth();
  const [restaurantIds, setRestaurantIds] = useState<string[]>([]);
  const [timeframe, setTimeframe] = useState<TimeframeType>('30');
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(undefined);
  const [chartType, setChartType] = useState<'bar' | 'line' | 'pie'>('bar');

  // Handle date picker value change
  const handleDatePickerChange = (value: string | DateRange) => {
    if (typeof value === 'string') {
      setTimeframe(value as TimeframeType);
      setCustomDateRange(undefined);
    } else {
      setTimeframe('custom');
      setCustomDateRange(value);
    }
  };

  // Use the unified hook for data fetching
  const {
    restaurants,
    usageData,
    loadingRestaurants,
    loadingUsage,
    error,
    refetch,
    clearError
  } = useSmsUsageReports({
    timeframe,
    selectedRestaurantIds: restaurantIds,
    customDateRange
  });
  
  // Colors for the charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A259FF', '#4CAF50', '#FB6D42', '#46BFBD'];

  // Handle restaurant selection from the selector component
  const handleRestaurantSelection = React.useCallback((selectedIds: string[]) => {
    console.log('[SMS Usage] Restaurant selection changed:', selectedIds);
    setRestaurantIds(selectedIds);
  }, []);

  // Prepare data for restaurant comparison chart
  const getRestaurantComparisonData = useMemo(() => {
    if (!usageData?.by_restaurant) return [];
    
    return usageData.by_restaurant
      .filter((restaurant) => {
        // If no specific restaurants selected, show all (including org campaigns)
        if (restaurantIds.length === 0) return true;
        
        // If specific restaurants selected, show only those restaurants
        // Organisation campaigns (with org_ prefix) are only shown when no specific selection is made
        if (restaurant.restaurant_id.startsWith('org_')) {
          return false; // Don't show org campaigns when specific restaurants are selected
        }
        
        return restaurantIds.includes(restaurant.restaurant_id);
      })
      .map((restaurant) => ({
        name: restaurant.restaurant_name,
        value: restaurant.total_units || 0
      }));
  }, [usageData, restaurantIds]);

  // Prepare data for timeline chart
  const getTimelineData = useMemo(() => {
    if (!usageData?.by_restaurant) return [];
    
    // Create a map to aggregate data by date
    const dateMap = new Map();
    
    // Process each restaurant's records
    usageData.by_restaurant
      .filter((restaurant) => {
        // If no specific restaurants selected, show all (including org campaigns)
        if (restaurantIds.length === 0) return true;
        
        // If specific restaurants selected, show only those restaurants
        // Organisation campaigns (with org_ prefix) are only shown when no specific selection is made
        if (restaurant.restaurant_id.startsWith('org_')) {
          return false; // Don't show org campaigns when specific restaurants are selected
        }
        
        return restaurantIds.includes(restaurant.restaurant_id);
      })
      .forEach((restaurant) => {
        (restaurant.records || []).forEach((record) => {
          const date = record.date;
          const current = dateMap.get(date) || { date, total: 0 };
          current.total += record.units || 0;
          current[restaurant.restaurant_name] = record.units || 0;
          dateMap.set(date, current);
        });
      });
    
    // Convert map to array and sort by date
    const result = Array.from(dateMap.values());
    result.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    
    return result;
  }, [usageData, restaurantIds]);

  // Format date for display in charts
  const formatChartDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'MMM dd');
    } catch (e) {
      return dateString;
    }
  };

  // Export usage data as CSV
  const exportAsCSV = () => {
    try {
      if (!usageData?.by_restaurant || !user?.organisationId) {
        toast({
          title: "No data to export",
          description: "There is no usage data available to export.",
          variant: "destructive"
        });
        return;
      }
      
      // Create CSV header
      let csvContent = "Restaurant,Date,SMS Segments\n";
      
      // Add data rows
      usageData.by_restaurant
        .filter((restaurant) => {
          // If no specific restaurants selected, show all (including org campaigns)
          if (restaurantIds.length === 0) return true;
          
          // If specific restaurants selected, show only those restaurants
          // Organisation campaigns (with org_ prefix) are only shown when no specific selection is made
          if (restaurant.restaurant_id.startsWith('org_')) {
            return false; // Don't show org campaigns when specific restaurants are selected
          }
          
          return restaurantIds.includes(restaurant.restaurant_id);
        })
        .forEach((restaurant) => {
          (restaurant.records || []).forEach((record) => {
            csvContent += `"${restaurant.restaurant_name}","${record.date}",${record.units || 0}\n`;
          });
        });
      
      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `sms-usage-${user.organisationId}-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "Export Successful",
        description: "Usage data has been downloaded as CSV",
      });
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast({
        title: "Failed to export data",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    }
  };

  // Calculate aggregate statistics
  const aggregateStats = useMemo(() => {
    if (!usageData?.by_restaurant) {
      return {
        messageCount: 0,
        units: 0,
        estimatedCost: '0.00'
      };
    }

    let totalMessages = 0;
    let totalUnits = 0;
    
    // Filter by selected restaurants and calculate totals
    const filteredRestaurants = usageData.by_restaurant.filter((restaurant) => {
      // If no specific restaurants selected, show all (including org campaigns)
      if (restaurantIds.length === 0) return true;
      
      // If specific restaurants selected, show only those restaurants
      // Organisation campaigns (with org_ prefix) are only shown when no specific selection is made
      if (restaurant.restaurant_id.startsWith('org_')) {
        return false; // Don't show org campaigns when specific restaurants are selected
      }
      
      return restaurantIds.includes(restaurant.restaurant_id);
    });
    
    filteredRestaurants.forEach((restaurant) => {
      totalUnits += restaurant.total_units || 0;
      // Use total_message_count if available, otherwise calculate from records
      if (restaurant.total_message_count !== undefined) {
        totalMessages += restaurant.total_message_count;
      } else {
        const restaurantMessages = (restaurant.records || []).reduce((sum, record) =>
          sum + (record.message_count || 1), 0
        );
        totalMessages += restaurantMessages;
      }
    });
    
    const estimatedCost = (totalUnits * 0.15).toFixed(2); // Default price calculation
    
    return {
      messageCount: totalMessages,
      units: totalUnits,
      estimatedCost
    };
  }, [usageData, restaurantIds]);
  
  // Get number of restaurants with SMS enabled
  const enabledCount = useMemo(() => {
    return restaurants.filter(r => r.smsEnabled).length;
  }, [restaurants]);

  // Handle errors with user-friendly messages
  React.useEffect(() => {
    if (error) {
      console.error('SMS Usage Error:', error);
    }
  }, [error]);

  const isLoading = loadingRestaurants || loadingUsage;

  return (
    <div className="space-y-6">
      {/* Restaurant selector */}
      <UsageReportSelector 
        restaurants={restaurants}
        loading={loadingRestaurants}
        onSelectRestaurants={handleRestaurantSelection}
      />
      
      {/* Error display */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-red-800">
                <p className="font-medium">Error loading data</p>
                <p className="text-sm">{error}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  clearError();
                  refetch();
                }}
              >
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Usage summary card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="text-lg">
                SMS Usage Summary
              </CardTitle>
              <CardDescription>
                {restaurantIds.length > 0 ? `${restaurantIds.length} restaurant${restaurantIds.length !== 1 ? 's' : ''} selected` : 'Organisation-wide data'}
              </CardDescription>
            </div>

            <div className="flex flex-wrap gap-2 items-center">
              {/* Timeframe selection */}
              <EnhancedDateRangePicker
                value={timeframe === 'custom' && customDateRange ? customDateRange : timeframe}
                onValueChange={handleDatePickerChange}
              />

              {/* Chart type selection */}
              <div className="flex rounded-md shadow-sm">
                <Button
                  variant={chartType === 'bar' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setChartType('bar')}
                  className="rounded-l-md rounded-r-none"
                >
                  <BarChart3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={chartType === 'line' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setChartType('line')}
                  className="rounded-none border-l-0 border-r-0"
                >
                  <BarChart4 className="h-4 w-4" />
                </Button>
                <Button
                  variant={chartType === 'pie' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setChartType('pie')}
                  className="rounded-r-md rounded-l-none"
                >
                  <Calendar className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Actions */}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={refetch}
                disabled={isLoading}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={exportAsCSV}
                disabled={isLoading || !usageData}
              >
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        
        {isLoading ? (
          <CardContent className="p-6">
            <div className="flex justify-center items-center py-4">
              <Loader className="h-5 w-5 animate-spin mr-2" />
              <span>Loading usage statistics...</span>
            </div>
          </CardContent>
        ) : (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatsCard 
                icon={<MessageSquare className="h-5 w-5 text-blue-500" />}
                title="SMS Messages Sent"
                value={aggregateStats.messageCount.toString()}
                description={`Across ${enabledCount} enabled restaurant${enabledCount !== 1 ? 's' : ''}`}
              />
              
              <StatsCard 
                icon={<Activity className="h-5 w-5 text-green-500" />}
                title="SMS Units Used"
                value={aggregateStats.units.toString()}
                description="Total SMS billing units"
              />
              
              <StatsCard
                icon={<DollarSign className="h-5 w-5 text-amber-500" />}
                title="Estimated Cost"
                value={`$${aggregateStats.estimatedCost}`}
                description="Based on selected time period"
              />
            </div>
            
            {restaurants.length > 0 && (!usageData?.by_restaurant || usageData.by_restaurant.length === 0) && (
              <div className="mt-4 p-4 bg-muted rounded-md text-center">
                <p className="text-sm text-muted-foreground">
                  No usage data available for the selected restaurants in this time period.
                </p>
              </div>
            )}
          </CardContent>
        )}
      </Card>
      
      {/* Charts section */}
      {usageData?.by_restaurant && !isLoading && getTimelineData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">SMS Usage Timeline</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                {chartType === 'line' ? (
                  <LineChart
                    data={getTimelineData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={formatChartDate}
                    />
                    <YAxis />
                    <Tooltip 
                      labelFormatter={(value) => format(parseISO(value), 'MMMM dd, yyyy')}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="total" 
                      stroke="#8884d8" 
                      name="Total SMS"
                      strokeWidth={2}
                    />
                  </LineChart>
                ) : chartType === 'bar' ? (
                  <BarChart
                    data={getTimelineData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={formatChartDate}
                    />
                    <YAxis />
                    <Tooltip 
                      labelFormatter={(value) => format(parseISO(value), 'MMMM dd, yyyy')}
                    />
                    <Legend />
                    <Bar 
                      dataKey="total" 
                      fill="#8884d8" 
                      name="Total SMS" 
                    />
                  </BarChart>
                ) : (
                  <PieChart>
                    <Pie
                      data={getRestaurantComparisonData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={130}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {getRestaurantComparisonData.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value} segments`, 'Usage']} />
                    <Legend />
                  </PieChart>
                )}
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Restaurant comparison chart */}
      {chartType !== 'pie' && usageData?.by_restaurant && getRestaurantComparisonData.length > 1 && !isLoading && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Restaurant Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={getRestaurantComparisonData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    width={150}
                  />
                  <Tooltip formatter={(value) => [`${value} segments`, 'Usage']} />
                  <Legend />
                  <Bar 
                    dataKey="value" 
                    name="SMS Segments" 
                    fill="#8884d8"
                  >
                    {getRestaurantComparisonData.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
});

interface StatsCardProps {
  icon: React.ReactNode;
  title: string;
  value: string;
  description: string;
}

const StatsCard: React.FC<StatsCardProps> = ({ icon, title, value, description }) => {
  return (
    <div className="bg-muted/40 p-4 rounded-md">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <h3 className="font-medium text-sm">{title}</h3>
      </div>
      <div className="mt-1">
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      </div>
    </div>
  );
};